// ============================================================
// SEGUIMIENTO MACROS — flujo: foto → IA → editar gramos/ml → confirmar → guarda
// ============================================================
import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/SeguimientoMacros.css';
import { API_URL } from '../config';

// ─── Tipos ───────────────────────────────────────────────────
type NombreUsuario = 'Gisbel' | 'Lourdes' | 'Ronald' | 'Liseth';
type TipoComida = 'desayuno' | 'almuerzo' | 'cena' | 'snack';
type Periodo = 'diario' | 'semanal' | 'mensual';

interface RegistroMacro {
    id: number;
    nombre_usuario: NombreUsuario;
    nombre_comida: string;
    calorias: number;
    proteinas: number;
    carbohidratos: number;
    grasas: number;
    fibra: number;
    azucar: number;
    porcion_gramos: number;
    volumen_ml: number;
    unidad_medida: string;
    cantidad: number;
    es_liquido: boolean;
    editado_usuario: boolean;
    fuente: string;
    tipo_comida: TipoComida;
    fecha_registro: string;
    url_evidencia: string | null;
    fecha_subida: string;
    notas_ia: string;
}

interface Borrador {
    nombre_comida: string;
    calorias: number;
    proteinas: number;
    carbohidratos: number;
    grasas: number;
    fibra: number;
    azucar: number;
    porcion_gramos: number;
    volumen_ml: number;
    es_liquido: boolean;
    notas: string;
    url_relativa: string;
    tipo_comida: string;
    nombre_usuario: string;
    unidad_medida: string;
}

interface ResumenDia {
    fecha: string;
    calorias: number;
    proteinas: number;
    carbohidratos: number;
    grasas: number;
    fibra: number;
    azucar: number;
    num_comidas: number;
}

interface ResumenData {
    periodo: string;
    usuario: string;
    totales: {
        total_calorias: number;
        total_proteinas: number;
        total_carbohidratos: number;
        total_grasas: number;
        total_fibra: number;
        total_azucar: number;
        num_comidas: number;
    };
    por_dia: ResumenDia[];
}

type Estado = 'idle' | 'analizando' | 'borrador' | 'guardando' | 'ok' | 'error_ia' | 'error_nd';

// ─── Constantes ──────────────────────────────────────────────
const USUARIOS: NombreUsuario[] = ['Gisbel', 'Lourdes', 'Ronald', 'Liseth'];

const AVATAR_COLORES: Record<NombreUsuario, string> = {
    Gisbel: '#e879a0',
    Lourdes: '#a78bfa',
    Ronald: '#38bdf8',
    Liseth: '#34d399',
};

const TIPOS_COMIDA: { value: TipoComida; label: string; icon: string }[] = [
    { value: 'desayuno', label: 'Desayuno', icon: '🌅' },
    { value: 'almuerzo', label: 'Almuerzo', icon: '☀️' },
    { value: 'cena', label: 'Cena', icon: '🌙' },
    { value: 'snack', label: 'Snack', icon: '🍎' },
];

const PERIODOS: { value: Periodo; label: string }[] = [
    { value: 'diario', label: 'Hoy' },
    { value: 'semanal', label: 'Semana' },
    { value: 'mensual', label: 'Mes' },
];

// ─── Componente ──────────────────────────────────────────────
const SeguimientoMacros: React.FC = () => {
    const [usuario, setUsuario] = useState<NombreUsuario>('Ronald');
    const [tipoComida, setTipoComida] = useState<TipoComida>('almuerzo');
    const [preview, setPreview] = useState<string | null>(null);
    const [dragging, setDragging] = useState(false);
    const [estado, setEstado] = useState<Estado>('idle');
    const [mensajeError, setMensajeError] = useState<string>('');
    const [borrador, setBorrador] = useState<Borrador | null>(null);
    const [editadoPorUsuario, setEditadoPorUsuario] = useState(false);
    const [registros, setRegistros] = useState<RegistroMacro[]>([]);
    const [cargando, setCargando] = useState(true);
    const [periodo, setPeriodo] = useState<Periodo>('diario');
    const [resumen, setResumen] = useState<ResumenData | null>(null);
    const [vista, setVista] = useState<'registrar' | 'resumen'>('registrar');
    const [editandoId, setEditandoId] = useState<number | null>(null);
    const [editForm, setEditForm] = useState<Partial<RegistroMacro>>({});
    const inputFileRef = useRef<HTMLInputElement>(null);
    const archivoRef = useRef<File | null>(null);

    // ── Cargar historial del día ──────────────────────────────
    const cargarRegistros = useCallback(async () => {
        setCargando(true);
        try {
            const hoy = new Date().toLocaleDateString('en-CA');
            const res = await fetch(`${API_URL}/api/macros/listar/?usuario=${usuario}&fecha=${hoy}`);
            if (res.ok) setRegistros(await res.json());
        } catch { /* silencioso */ }
        finally { setCargando(false); }
    }, [usuario]);

    // ── Cargar resumen ────────────────────────────────────────
    const cargarResumen = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/macros/resumen/?usuario=${usuario}&periodo=${periodo}`);
            if (res.ok) setResumen(await res.json());
        } catch { /* silencioso */ }
    }, [usuario, periodo]);

    useEffect(() => { cargarRegistros(); }, [cargarRegistros]);
    useEffect(() => { cargarResumen(); }, [cargarResumen]);

    // ── Analizar imagen (NO guarda, devuelve borrador) ────────
    const analizarImagen = useCallback(async (file: File, gramosOverride?: number, mlOverride?: number) => {
        setPreview(URL.createObjectURL(file));
        archivoRef.current = file;
        setEstado('analizando');
        setBorrador(null);
        setMensajeError('');
        setEditadoPorUsuario(false);

        try {
            const fd = new FormData();
            fd.append('evidencia', file);
            fd.append('nombre_usuario', usuario);
            fd.append('tipo_comida', tipoComida);
            if (gramosOverride) fd.append('porcion_gramos', String(gramosOverride));
            if (mlOverride) fd.append('volumen_ml', String(mlOverride));

            const res = await fetch(`${API_URL}/api/macros/analizar/`, { method: 'POST', body: fd });
            const data = await res.json();

            if (!res.ok) {
                setEstado('error_ia');
                setMensajeError(data.error || 'Error al analizar la imagen.');
                return;
            }

            if (data.nombre_comida === 'No identificado' && data.calorias === 0) {
                setEstado('error_nd');
                setMensajeError('La IA no pudo identificar la comida. Intenta con una foto más clara.');
                return;
            }

            setBorrador(data);
            setEstado('borrador');
        } catch {
            setEstado('error_ia');
            setMensajeError('Error de conexión. Verifica tu red.');
        }
    }, [usuario, tipoComida]);

    // ── Re-analizar con gramos/ml corregidos ──────────────────
    const reAnalizar = () => {
        if (!archivoRef.current || !borrador) return;
        const gramos = borrador.es_liquido ? undefined : borrador.porcion_gramos;
        const ml = borrador.es_liquido ? borrador.volumen_ml : undefined;
        analizarImagen(archivoRef.current, gramos, ml);
    };

    // ── Confirmar y guardar borrador ──────────────────────────
    const confirmarGuardar = async () => {
        if (!borrador) return;
        setEstado('guardando');

        try {
            const body = {
                nombre_usuario: usuario,
                nombre_comida: borrador.nombre_comida,
                calorias: borrador.calorias,
                proteinas: borrador.proteinas,
                carbohidratos: borrador.carbohidratos,
                grasas: borrador.grasas,
                fibra: borrador.fibra,
                azucar: borrador.azucar,
                porcion_gramos: borrador.porcion_gramos,
                volumen_ml: borrador.volumen_ml,
                es_liquido: borrador.es_liquido,
                tipo_comida: tipoComida,
                url_relativa: borrador.url_relativa,
                notas: borrador.notas,
                editado_usuario: editadoPorUsuario,
                fuente: 'ia',
                cantidad: 1,
                fecha_registro: new Date().toLocaleDateString('en-CA'),
            };

            const res = await fetch(`${API_URL}/api/macros/guardar/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            if (res.ok) {
                setEstado('ok');
                cargarRegistros();
                cargarResumen();
            } else {
                const data = await res.json();
                setEstado('error_ia');
                setMensajeError(data.error || 'Error al guardar.');
            }
        } catch {
            setEstado('error_ia');
            setMensajeError('Error de conexión al guardar.');
        }
    };

    // ── Editar registro existente ─────────────────────────────
    const iniciarEdicion = (r: RegistroMacro) => {
        setEditandoId(r.id);
        setEditForm({ ...r });
    };

    const guardarEdicion = async () => {
        if (!editandoId || !editForm) return;
        try {
            const res = await fetch(`${API_URL}/api/macros/${editandoId}/editar/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editForm),
            });
            if (res.ok) {
                setEditandoId(null);
                setEditForm({});
                cargarRegistros();
                cargarResumen();
            }
        } catch { /* silencioso */ }
    };

    // ── Eliminar registro ─────────────────────────────────────
    const eliminarRegistro = async (id: number) => {
        if (!window.confirm('¿Eliminar este registro?')) return;
        try {
            await fetch(`${API_URL}/api/macros/${id}/eliminar/`, { method: 'DELETE' });
            cargarRegistros();
            cargarResumen();
        } catch { /* silencioso */ }
    };

    // ── Manejo de archivo ────────────────────────────────────
    const aplicarArchivo = (file: File) => {
        if (!file.type.startsWith('image/') && !file.name.toLowerCase().match(/\.(heic|heif)$/i)) return;
        analizarImagen(file);
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) aplicarArchivo(f);
        e.target.value = '';
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) aplicarArchivo(f);
    };

    // ── Reset ────────────────────────────────────────────────
    const resetear = () => {
        setPreview(null);
        setEstado('idle');
        setBorrador(null);
        setMensajeError('');
        setEditadoPorUsuario(false);
        archivoRef.current = null;
    };

    // ── Helpers ──────────────────────────────────────────────
    const tipoLabel = (t: string) => TIPOS_COMIDA.find(tc => tc.value === t)?.label || t;
    const tipoIcon = (t: string) => TIPOS_COMIDA.find(tc => tc.value === t)?.icon || '🍽️';

    // Actualizar campo del borrador
    const updateBorrador = (campo: keyof Borrador, valor: any) => {
        if (!borrador) return;
        setEditadoPorUsuario(true);
        setBorrador({ ...borrador, [campo]: valor });
    };

    // Totales del día (de registros cargados)
    const totalesDia = registros.reduce(
        (acc, r) => ({
            calorias: acc.calorias + r.calorias,
            proteinas: acc.proteinas + r.proteinas,
            carbohidratos: acc.carbohidratos + r.carbohidratos,
            grasas: acc.grasas + r.grasas,
        }),
        { calorias: 0, proteinas: 0, carbohidratos: 0, grasas: 0 }
    );

    return (
        <div className="macros-page">
            {/* Header */}
            <header className="macros-header">
                <div className="macros-header-inner">
                    <span className="macros-header-icon">🥗</span>
                    <div>
                        <h1 className="macros-header-title">Control de Macros</h1>
                        <p className="macros-header-sub">Foto → IA analiza → corrige gramos/ml → confirma y guarda</p>
                    </div>
                </div>
            </header>

            {/* Tabs vista */}
            <div className="macros-tabs-wrapper">
                <div className="macros-tabs">
                    <button
                        className={`macros-tab ${vista === 'registrar' ? 'macros-tab--active' : ''}`}
                        onClick={() => setVista('registrar')}
                    >📸 Registrar</button>
                    <button
                        className={`macros-tab ${vista === 'resumen' ? 'macros-tab--active' : ''}`}
                        onClick={() => setVista('resumen')}
                    >📊 Resumen</button>
                </div>
            </div>

            <main className="macros-main">
                {vista === 'registrar' ? (
                    <>
                        {/* ─── Panel subida ──────────────────────── */}
                        <section className="macros-card macros-form-card">
                            <h2 className="macros-section-title">📸 Registrar comida</h2>

                            {/* Selector de usuario */}
                            <div className="macros-field">
                                <label className="macros-label">¿Quién eres?</label>
                                <div className="macros-pills">
                                    {USUARIOS.map(u => (
                                        <button
                                            key={u}
                                            type="button"
                                            className={`macros-pill ${usuario === u ? 'macros-pill--active' : ''}`}
                                            style={{ ['--pill-color' as any]: AVATAR_COLORES[u] }}
                                            onClick={() => { setUsuario(u); resetear(); }}
                                        >
                                            <span className="macros-pill-dot" style={{ background: AVATAR_COLORES[u] }} />
                                            {u}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Selector de tipo de comida */}
                            <div className="macros-field">
                                <label className="macros-label">Tipo de comida</label>
                                <div className="macros-pills">
                                    {TIPOS_COMIDA.map(tc => (
                                        <button
                                            key={tc.value}
                                            type="button"
                                            className={`macros-pill macros-pill-tipo ${tipoComida === tc.value ? 'macros-pill--active' : ''}`}
                                            style={{ ['--pill-color' as any]: '#f97316' }}
                                            onClick={() => setTipoComida(tc.value)}
                                        >
                                            <span>{tc.icon}</span> {tc.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Dropzone */}
                            <div className="macros-field">
                                <label className="macros-label">Foto de la comida</label>
                                <div
                                    className={`macros-dropzone ${dragging ? 'macros-dropzone--drag' : ''} ${preview ? 'macros-dropzone--has-preview' : ''}`}
                                    onDragOver={e => { e.preventDefault(); setDragging(true); }}
                                    onDragLeave={() => setDragging(false)}
                                    onDrop={handleDrop}
                                    onClick={() => estado !== 'analizando' && estado !== 'guardando' && inputFileRef.current?.click()}
                                >
                                    {preview ? (
                                        <img src={preview} alt="Preview" className="macros-preview-img" />
                                    ) : (
                                        <div className="macros-dropzone-inner">
                                            <span className="macros-dropzone-icon">📷</span>
                                            <span className="macros-dropzone-text">
                                                Arrastra tu foto aquí o <strong>haz clic para elegir</strong>
                                            </span>
                                            <span className="macros-dropzone-hint">JPG, PNG o HEIC · máx. 10 MB</span>
                                        </div>
                                    )}
                                    <input
                                        ref={inputFileRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={handleFileInput}
                                    />
                                </div>

                                {/* Estados */}
                                {estado === 'analizando' && (
                                    <div className="macros-status macros-status--analyzing">
                                        <span className="macros-spinner" />
                                        Analizando comida con IA…
                                    </div>
                                )}
                                {estado === 'guardando' && (
                                    <div className="macros-status macros-status--analyzing">
                                        <span className="macros-spinner" />
                                        Guardando registro…
                                    </div>
                                )}

                                {/* ─── Borrador editable ────────────── */}
                                {estado === 'borrador' && borrador && (
                                    <div className="macros-status macros-status--draft">
                                        <div className="macros-draft-card">
                                            <div className="macros-draft-header">
                                                <span className="macros-draft-icon">🤖</span>
                                                <div>
                                                    <strong>Estimación de la IA</strong>
                                                    <span className="macros-draft-hint">Edita los valores antes de guardar</span>
                                                </div>
                                            </div>

                                            {/* Nombre */}
                                            <div className="macros-draft-field">
                                                <label className="macros-draft-label">Nombre</label>
                                                <input
                                                    type="text"
                                                    className="macros-draft-input"
                                                    value={borrador.nombre_comida}
                                                    onChange={e => updateBorrador('nombre_comida', e.target.value)}
                                                />
                                            </div>

                                            {/* Toggle sólido/líquido */}
                                            <div className="macros-draft-field">
                                                <label className="macros-draft-label">Tipo</label>
                                                <div className="macros-toggle-wrap">
                                                    <button
                                                        className={`macros-toggle-btn ${!borrador.es_liquido ? 'macros-toggle-btn--active' : ''}`}
                                                        onClick={() => updateBorrador('es_liquido', false)}
                                                    >🍽️ Sólido (gramos)</button>
                                                    <button
                                                        className={`macros-toggle-btn ${borrador.es_liquido ? 'macros-toggle-btn--active' : ''}`}
                                                        onClick={() => updateBorrador('es_liquido', true)}
                                                    >🥤 Líquido (ml)</button>
                                                </div>
                                            </div>

                                            {/* Cantidad: gramos o ml */}
                                            <div className="macros-draft-field">
                                                <label className="macros-draft-label">
                                                    {borrador.es_liquido ? 'Volumen (ml)' : 'Peso (gramos)'}
                                                </label>
                                                <div className="macros-draft-quantity-row">
                                                    <input
                                                        type="number"
                                                        className="macros-draft-input macros-draft-input--quantity"
                                                        value={borrador.es_liquido ? borrador.volumen_ml : borrador.porcion_gramos}
                                                        onChange={e => {
                                                            const val = parseInt(e.target.value) || 0;
                                                            if (borrador.es_liquido) {
                                                                updateBorrador('volumen_ml', val);
                                                            } else {
                                                                updateBorrador('porcion_gramos', val);
                                                            }
                                                        }}
                                                        min={0}
                                                    />
                                                    <span className="macros-draft-unit">{borrador.es_liquido ? 'ml' : 'g'}</span>
                                                    <button
                                                        className="macros-btn-reanalyze"
                                                        onClick={reAnalizar}
                                                        title="Re-analizar con la cantidad corregida"
                                                    >🔄 Re-analizar</button>
                                                </div>
                                            </div>

                                            {/* Grid de macros editables */}
                                            <div className="macros-draft-grid">
                                                <div className="macros-draft-macro macros-draft-cal">
                                                    <label>kcal</label>
                                                    <input type="number" value={borrador.calorias} onChange={e => updateBorrador('calorias', parseInt(e.target.value) || 0)} />
                                                </div>
                                                <div className="macros-draft-macro macros-draft-prot">
                                                    <label>Proteína (g)</label>
                                                    <input type="number" step="0.1" value={borrador.proteinas} onChange={e => updateBorrador('proteinas', parseFloat(e.target.value) || 0)} />
                                                </div>
                                                <div className="macros-draft-macro macros-draft-carb">
                                                    <label>Carbos (g)</label>
                                                    <input type="number" step="0.1" value={borrador.carbohidratos} onChange={e => updateBorrador('carbohidratos', parseFloat(e.target.value) || 0)} />
                                                </div>
                                                <div className="macros-draft-macro macros-draft-fat">
                                                    <label>Grasas (g)</label>
                                                    <input type="number" step="0.1" value={borrador.grasas} onChange={e => updateBorrador('grasas', parseFloat(e.target.value) || 0)} />
                                                </div>
                                            </div>

                                            {/* Notas */}
                                            {borrador.notas && (
                                                <p className="macros-result-notes">💡 {borrador.notas}</p>
                                            )}

                                            {/* Botones */}
                                            <div className="macros-draft-actions">
                                                <button className="macros-btn-confirm" onClick={confirmarGuardar}>
                                                    ✅ Confirmar y guardar
                                                </button>
                                                <button className="macros-btn-cancel" onClick={resetear}>
                                                    ✕ Cancelar
                                                </button>
                                            </div>
                                            {editadoPorUsuario && (
                                                <span className="macros-draft-edited-badge">✏️ Editado por ti</span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {estado === 'ok' && (
                                    <div className="macros-status macros-status--ok">
                                        ✅ ¡Registro guardado correctamente!
                                        <button className="macros-btn-reset" onClick={resetear}>Subir otra foto</button>
                                    </div>
                                )}
                                {estado === 'error_nd' && (
                                    <div className="macros-status macros-status--warn">
                                        ⚠️ {mensajeError}
                                        <button className="macros-btn-reset" onClick={resetear}>Intentar de nuevo</button>
                                    </div>
                                )}
                                {estado === 'error_ia' && (
                                    <div className="macros-status macros-status--error">
                                        ❌ {mensajeError}
                                        <button className="macros-btn-reset" onClick={resetear}>Intentar de nuevo</button>
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* ─── Resumen rápido del día ─────────────── */}
                        {registros.length > 0 && (
                            <section className="macros-card macros-summary-card">
                                <h2 className="macros-section-title">📊 Resumen del día — {usuario}</h2>
                                <div className="macros-day-totals">
                                    <div className="macros-total-item macros-total-cal">
                                        <span className="macros-total-value">{totalesDia.calorias}</span>
                                        <span className="macros-total-label">kcal</span>
                                    </div>
                                    <div className="macros-total-item macros-total-prot">
                                        <span className="macros-total-value">{totalesDia.proteinas.toFixed(1)}</span>
                                        <span className="macros-total-label">Proteína (g)</span>
                                    </div>
                                    <div className="macros-total-item macros-total-carb">
                                        <span className="macros-total-value">{totalesDia.carbohidratos.toFixed(1)}</span>
                                        <span className="macros-total-label">Carbos (g)</span>
                                    </div>
                                    <div className="macros-total-item macros-total-fat">
                                        <span className="macros-total-value">{totalesDia.grasas.toFixed(1)}</span>
                                        <span className="macros-total-label">Grasas (g)</span>
                                    </div>
                                </div>
                            </section>
                        )}

                        {/* ─── Feed de comidas del día ────────────── */}
                        <section className="macros-card macros-feed-card">
                            <h2 className="macros-section-title">🍽️ Comidas de hoy — {usuario}</h2>
                            {cargando ? (
                                <div className="macros-loading">Cargando registros…</div>
                            ) : registros.length === 0 ? (
                                <div className="macros-empty">Todavía no hay comidas registradas hoy. ¡Sube tu primera foto!</div>
                            ) : (
                                <div className="macros-feed">
                                    {registros.map(r => (
                                        <article key={r.id} className="macros-entry">
                                            {editandoId === r.id ? (
                                                /* ── Modo edición inline ── */
                                                <div className="macros-entry-edit">
                                                    <div className="macros-entry-edit-header">
                                                        <input
                                                            type="text"
                                                            className="macros-draft-input"
                                                            value={editForm.nombre_comida || ''}
                                                            onChange={e => setEditForm({ ...editForm, nombre_comida: e.target.value })}
                                                            placeholder="Nombre"
                                                        />
                                                    </div>
                                                    <div className="macros-draft-quantity-row" style={{ marginBottom: 8 }}>
                                                        <input
                                                            type="number"
                                                            className="macros-draft-input macros-draft-input--quantity"
                                                            value={editForm.es_liquido ? (editForm.volumen_ml || 0) : (editForm.porcion_gramos || 0)}
                                                            onChange={e => {
                                                                const val = parseInt(e.target.value) || 0;
                                                                if (editForm.es_liquido) {
                                                                    setEditForm({ ...editForm, volumen_ml: val });
                                                                } else {
                                                                    setEditForm({ ...editForm, porcion_gramos: val });
                                                                }
                                                            }}
                                                        />
                                                        <span className="macros-draft-unit">{editForm.es_liquido ? 'ml' : 'g'}</span>
                                                    </div>
                                                    <div className="macros-draft-grid">
                                                        <div className="macros-draft-macro macros-draft-cal">
                                                            <label>kcal</label>
                                                            <input type="number" value={editForm.calorias || 0} onChange={e => setEditForm({ ...editForm, calorias: parseInt(e.target.value) || 0 })} />
                                                        </div>
                                                        <div className="macros-draft-macro macros-draft-prot">
                                                            <label>Prot</label>
                                                            <input type="number" step="0.1" value={editForm.proteinas || 0} onChange={e => setEditForm({ ...editForm, proteinas: parseFloat(e.target.value) || 0 })} />
                                                        </div>
                                                        <div className="macros-draft-macro macros-draft-carb">
                                                            <label>Carb</label>
                                                            <input type="number" step="0.1" value={editForm.carbohidratos || 0} onChange={e => setEditForm({ ...editForm, carbohidratos: parseFloat(e.target.value) || 0 })} />
                                                        </div>
                                                        <div className="macros-draft-macro macros-draft-fat">
                                                            <label>Gras</label>
                                                            <input type="number" step="0.1" value={editForm.grasas || 0} onChange={e => setEditForm({ ...editForm, grasas: parseFloat(e.target.value) || 0 })} />
                                                        </div>
                                                    </div>
                                                    <div className="macros-entry-edit-actions">
                                                        <button className="macros-btn-confirm macros-btn-sm" onClick={guardarEdicion}>💾 Guardar</button>
                                                        <button className="macros-btn-cancel macros-btn-sm" onClick={() => { setEditandoId(null); setEditForm({}); }}>✕</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                /* ── Vista normal ── */
                                                <>
                                                    <div className="macros-entry-left">
                                                        <div className="macros-entry-tipo" title={tipoLabel(r.tipo_comida)}>
                                                            {tipoIcon(r.tipo_comida)}
                                                        </div>
                                                        <div className="macros-entry-info">
                                                            <span className="macros-entry-name">
                                                                {r.nombre_comida}
                                                                <span className={`macros-badge macros-badge--${r.fuente}`}>
                                                                    {r.fuente === 'ia' ? '🤖 IA' : '✍️ Manual'}
                                                                </span>
                                                            </span>
                                                            <span className="macros-entry-meta">
                                                                {tipoLabel(r.tipo_comida)} · {r.es_liquido ? `${r.volumen_ml} ml` : `${r.porcion_gramos} g`}
                                                                {r.editado_usuario && <span className="macros-edited-dot" title="Editado">✏️</span>}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="macros-entry-macros">
                                                        <span className="macros-tag macros-tag-cal">{r.calorias} kcal</span>
                                                        <span className="macros-tag macros-tag-prot">{r.proteinas}g prot</span>
                                                        <span className="macros-tag macros-tag-carb">{r.carbohidratos}g carb</span>
                                                        <span className="macros-tag macros-tag-fat">{r.grasas}g gras</span>
                                                    </div>

                                                    {r.url_evidencia && (
                                                        <a href={r.url_evidencia} target="_blank" rel="noopener noreferrer" className="macros-entry-thumb">
                                                            <img src={r.url_evidencia} alt="Comida" />
                                                            <span className="macros-thumb-overlay">Ver</span>
                                                        </a>
                                                    )}

                                                    <div className="macros-entry-actions">
                                                        <button className="macros-action-btn macros-action-edit" onClick={() => iniciarEdicion(r)} title="Editar">✏️</button>
                                                        <button className="macros-action-btn macros-action-delete" onClick={() => eliminarRegistro(r.id)} title="Eliminar">🗑️</button>
                                                    </div>
                                                </>
                                            )}
                                        </article>
                                    ))}
                                </div>
                            )}
                        </section>
                    </>
                ) : (
                    /* ─── Vista Resumen ────────────────────────── */
                    <>
                        <section className="macros-card macros-resumen-card">
                            <h2 className="macros-section-title">📊 Resumen de macros — {usuario}</h2>

                            {/* Selector de usuario para resumen */}
                            <div className="macros-field">
                                <label className="macros-label">Usuario</label>
                                <div className="macros-pills">
                                    {USUARIOS.map(u => (
                                        <button
                                            key={u}
                                            type="button"
                                            className={`macros-pill ${usuario === u ? 'macros-pill--active' : ''}`}
                                            style={{ ['--pill-color' as any]: AVATAR_COLORES[u] }}
                                            onClick={() => setUsuario(u)}
                                        >
                                            <span className="macros-pill-dot" style={{ background: AVATAR_COLORES[u] }} />
                                            {u}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Selector de período */}
                            <div className="macros-field">
                                <label className="macros-label">Período</label>
                                <div className="macros-pills">
                                    {PERIODOS.map(p => (
                                        <button
                                            key={p.value}
                                            type="button"
                                            className={`macros-pill macros-pill-tipo ${periodo === p.value ? 'macros-pill--active' : ''}`}
                                            style={{ ['--pill-color' as any]: '#10b981' }}
                                            onClick={() => setPeriodo(p.value)}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Totales del período */}
                            {resumen && (
                                <>
                                    <div className="macros-resumen-totals">
                                        <div className="macros-total-item macros-total-cal">
                                            <span className="macros-total-value">{resumen.totales.total_calorias}</span>
                                            <span className="macros-total-label">kcal totales</span>
                                        </div>
                                        <div className="macros-total-item macros-total-prot">
                                            <span className="macros-total-value">{resumen.totales.total_proteinas.toFixed(1)}</span>
                                            <span className="macros-total-label">Proteína (g)</span>
                                        </div>
                                        <div className="macros-total-item macros-total-carb">
                                            <span className="macros-total-value">{resumen.totales.total_carbohidratos.toFixed(1)}</span>
                                            <span className="macros-total-label">Carbos (g)</span>
                                        </div>
                                        <div className="macros-total-item macros-total-fat">
                                            <span className="macros-total-value">{resumen.totales.total_grasas.toFixed(1)}</span>
                                            <span className="macros-total-label">Grasas (g)</span>
                                        </div>
                                    </div>
                                    <div className="macros-resumen-extra">
                                        <span>🍽️ {resumen.totales.num_comidas} comidas</span>
                                        <span>🥬 {resumen.totales.total_fibra.toFixed(1)}g fibra</span>
                                        <span>🍬 {resumen.totales.total_azucar.toFixed(1)}g azúcar</span>
                                    </div>

                                    {/* Tabla por día */}
                                    {resumen.por_dia.length > 0 && (
                                        <div className="macros-table-wrapper">
                                            <table className="macros-table">
                                                <thead>
                                                    <tr>
                                                        <th>Fecha</th>
                                                        <th>kcal</th>
                                                        <th>Prot</th>
                                                        <th>Carbs</th>
                                                        <th>Grasas</th>
                                                        <th>Comidas</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {resumen.por_dia.map(d => (
                                                        <tr key={d.fecha}>
                                                            <td>{d.fecha}</td>
                                                            <td className="macros-td-cal">{d.calorias}</td>
                                                            <td className="macros-td-prot">{d.proteinas}g</td>
                                                            <td className="macros-td-carb">{d.carbohidratos}g</td>
                                                            <td className="macros-td-fat">{d.grasas}g</td>
                                                            <td>{d.num_comidas}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            )}
                            {resumen && resumen.totales.num_comidas === 0 && (
                                <div className="macros-empty">Sin registros para este período.</div>
                            )}
                        </section>
                    </>
                )}
            </main>
        </div>
    );
};

export default SeguimientoMacros;
